import {
  APIAttachment,
  APIDocumentArchiveFolder,
  APIDuplicateAttachmentGroup,
  ArdoqId,
  DocumentArchiveRenamePayload,
  TransformToGlobalPayload,
  TransformToGlobalResponse,
  UploadFilesPayload,
} from '@ardoq/api-types';
import {
  getArdoqErrorMessage,
  isArdoqError,
  resultsToPayloadOrError,
  toArdoqError,
} from '@ardoq/common-helpers';
import {
  CSRF_HEADER_NAME,
  Method,
  fetchAndRespondWith,
  fetchJson,
  getCsrfToken,
  headers,
  toRequestOptions,
} from './api';
import { folderApi } from './folderApi';
import { ApiResponse } from './types';

const getOrgUrl = (orgId: ArdoqId): string => `/api/attachment/org/${orgId}`;

const getItemFromOrgUrl = (orgId: ArdoqId, itemId: ArdoqId): string =>
  `${getOrgUrl(orgId)}/${itemId}`;

const getWorkspaceUrl = (workspaceId: ArdoqId): string =>
  `/api/attachment/workspace/${workspaceId}`;

const getItemFromWorkspaceUrl = (orgId: ArdoqId, itemId: ArdoqId): string =>
  `${getWorkspaceUrl(orgId)}/${itemId}`;

const transformToGlobal = ({
  workspaceId,
  attachmentAndFolderIds,
}: TransformToGlobalPayload): ApiResponse<TransformToGlobalResponse> => {
  const request = new Request(
    `/api/attachment/workspace/${workspaceId}/make-global`,
    toRequestOptions(Method.PUT, attachmentAndFolderIds)
  );
  return fetchJson<TransformToGlobalResponse>(request);
};

const getWorkspaceAttachments = (id: string): ApiResponse<APIAttachment[]> => {
  const request = new Request(
    `/api/attachment/workspace/${id}`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson<APIAttachment[]>(request);
};

const getOrgAttachments = (id: ArdoqId): ApiResponse<APIAttachment[]> => {
  const request = new Request(`/api/attachment/org/${id}`, { headers });
  return fetchJson<APIAttachment[]>(request);
};

const getDuplicates = (): ApiResponse<APIDuplicateAttachmentGroup[]> => {
  const request = new Request('/api/attachment/duplicates', { headers });
  return fetchJson<APIDuplicateAttachmentGroup[]>(request);
};

const uploadFiles = async ({
  files,
  type,
  typeId,
  folderId,
}: UploadFilesPayload): ApiResponse<APIAttachment[]> => {
  let url = `/api/attachment/${type}/${typeId}/upload`;
  if (folderId) {
    url = url.concat(`?folder=${folderId}`);
  }
  const token = getCsrfToken();
  const csrfHeader = token ? { [CSRF_HEADER_NAME]: token } : undefined;
  const requests = files.map(file => {
    const formData = new FormData();
    formData.append('attachment', file);
    return fetchJson<APIAttachment>(
      new Request(url, {
        method: Method.POST,
        headers: csrfHeader,
        body: formData,
      })
    );
  });
  const results = await Promise.all(requests);
  return resultsToPayloadOrError(results);
};
const rename = ({
  type,
  typeId,
  renameRequest,
}: DocumentArchiveRenamePayload): ApiResponse<APIAttachment> => {
  const request = new Request(
    `/api/attachment/${type}/${typeId}/${renameRequest._id}`, //
    toRequestOptions(Method.PATCH, renameRequest)
  );
  return fetchJson<APIAttachment>(request);
};

const deleteMultiple = async (
  attachmentIds: ArdoqId[]
): ApiResponse<ArdoqId[]> => {
  if (attachmentIds.length === 0) {
    return attachmentIds;
  }
  const request = new Request(
    `/api/attachment/bulk-delete`,
    toRequestOptions(Method.POST, { attachmentIds })
  );
  return fetchAndRespondWith(request, attachmentIds);
};

const moveMultipleToFolder = ({
  attachmentIds,
  folder,
}: {
  attachmentIds: ArdoqId[];
  folder: ArdoqId | null;
}): ApiResponse<APIAttachment[]> => {
  const request = new Request(
    `/api/attachment/move-to-folder`,
    toRequestOptions(Method.POST, { attachmentIds, folder })
  );
  return fetchJson<APIAttachment[]>(request);
};

const fetchAttachments = async (
  workspaceId: ArdoqId,
  organizationId: ArdoqId
): ApiResponse<APIAttachment[]> => {
  const responses = await Promise.all([
    getWorkspaceAttachments(workspaceId),
    getOrgAttachments(organizationId),
  ]);

  const results = resultsToPayloadOrError(
    responses,
    'Failed to fetch attachments'
  );

  if (isArdoqError(results)) {
    return results;
  }

  return results.flat();
};

const fetchFolders = async (
  workspaceId: ArdoqId,
  organizationId: ArdoqId
): ApiResponse<APIDocumentArchiveFolder[]> => {
  const responses = await Promise.all([
    folderApi.fetchWorkspaceFolders(workspaceId),
    folderApi.fetchOrgFolders(organizationId),
  ]);

  const results = resultsToPayloadOrError(responses, 'Failed to fetch folders');

  if (isArdoqError(results)) {
    return results;
  }

  return results.flat();
};

export type FetchAttachmentsAndFoldersResponse = {
  workspaceAttachments: APIAttachment[];
  workspaceFolders: APIDocumentArchiveFolder[];
};

const fetchAttachmentsAndFolders = async (
  workspaceId: ArdoqId
): ApiResponse<FetchAttachmentsAndFoldersResponse> => {
  const workspaceAttachmentsRequest = getWorkspaceAttachments(workspaceId);
  const workspaceFoldersRequest = folderApi.fetchWorkspaceFolders(workspaceId);

  const responses = await Promise.all([
    workspaceAttachmentsRequest,
    workspaceFoldersRequest,
  ]);
  const errors = responses.filter(isArdoqError);
  if (errors.length) {
    return toArdoqError({
      error: new AggregateError(
        errors,
        getArdoqErrorMessage(errors[0]) ||
          'Failed to fetch attachments and folders'
      ),
    });
  }
  // Type assertion here as TS is unable to infer the type of the response
  // even though any error would result in an early return
  return {
    workspaceAttachments: responses[0] as APIAttachment[],
    workspaceFolders: responses[1] as APIDocumentArchiveFolder[],
  };
};

export const attachmentApi = {
  transformToGlobal,
  getWorkspaceAttachments,
  getOrgAttachments,
  getDuplicates,
  getOrgUrl,
  getItemFromOrgUrl,
  getWorkspaceUrl,
  getItemFromWorkspaceUrl,
  uploadFiles,
  rename,
  deleteMultiple,
  moveMultipleToFolder,
  fetchAttachments,
  fetchFolders,
  fetchAttachmentsAndFolders,
};
